import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Clock, Gift, Copy, WhatsappLogo } from '@phosphor-icons/react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DoctorDashboard() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [referral, setReferral] = useState(null);

  useEffect(() => {
    fetchCases();
    fetchReferral();
  }, []);

  const fetchCases = async () => {
    try {
      const { data } = await axios.get(`${API}/cases`, { withCredentials: true });
      setCases(data.cases || []);
    } catch (error) {
      toast.error('Failed to fetch cases');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferral = async () => {
    try {
      const { data } = await axios.get(`${API}/doctors/me/referrals`, { withCredentials: true });
      setReferral(data);
    } catch (error) {
      console.error('Failed to fetch referral info');
    }
  };

  const referralLink = referral ? `${window.location.origin}/?ref=${referral.referral_code}` : '';

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied!');
  };

  const shareOnWhatsApp = () => {
    const message = `Looking for an expert second medical opinion? Visit SecondDoctorOpinion.com — Indian specialists registered with the NMC or a State Medical Council review your case within 48 hours. Use my referral link: ${referralLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const pendingCases = cases.filter(c => c.status === 'submitted' || c.status === 'under_review');
  const completedCases = cases.filter(c => c.status === 'opinion_ready');

  const getStatusLabel = (status) => {
    const labels = {
      submitted: 'New',
      under_review: 'In Progress',
      opinion_ready: 'Completed',
    };
    return labels[status] || status;
  };

  return (
    <div className="min-h-screen bg-surface-secondary" data-testid="doctor-dashboard">
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-manrope text-3xl font-bold text-primary mb-2">Dr. {user?.name}</h1>
              <p className="text-secondary">{user?.specialty} • {user?.credentials}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Link to="/doctor/practice" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-full font-medium text-sm hover:bg-primary/90" data-testid="my-practice-button">
                <i className="ph-bold ph-house" />My Practice
              </Link>
              <Link to={`/dr/${user?.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 border border-primary text-primary rounded-full font-medium text-sm hover:bg-primary/5" data-testid="public-profile-button">
                View Public Profile ↗
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
            <div className="card" data-testid="stat-total">
              <p className="text-sm text-secondary mb-1">Total Cases</p>
              <p className="text-3xl font-bold text-primary">{cases.length}</p>
            </div>
            <div className="card" data-testid="stat-pending">
              <p className="text-sm text-secondary mb-1">Pending Review</p>
              <p className="text-3xl font-bold text-accent">{pendingCases.length}</p>
            </div>
            <div className="card" data-testid="stat-completed">
              <p className="text-sm text-secondary mb-1">Completed</p>
              <p className="text-3xl font-bold" style={{ color: '#3B6953' }}>{completedCases.length}</p>
            </div>
          </div>

          {/* Referral Card */}
          {referral && (
            <div
              className="card mb-8"
              style={{ background: 'linear-gradient(135deg, #4A6750 0%, #38523D 100%)', color: 'white' }}
              data-testid="referral-card"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                  <div className="flex items-center gap-2 mb-2">
                    <Gift size={24} weight="duotone" className="text-accent" />
                    <h2 className="font-manrope text-xl font-semibold">Your Referral Link</h2>
                  </div>
                  <p className="text-sm text-white/80 mb-4">
                    Share with patients & colleagues. Earn ₹500 for every paid consultation booked through your link.
                  </p>
                  <div className="flex items-center gap-2 bg-white/10 rounded-lg p-3 mb-3">
                    <code className="text-sm font-mono flex-1 truncate" data-testid="referral-link-text">{referralLink}</code>
                    <button
                      onClick={copyReferralLink}
                      className="flex items-center gap-1 px-3 py-1.5 bg-accent text-white rounded-full text-xs font-medium hover:bg-accent/90 flex-shrink-0"
                      data-testid="copy-referral-button"
                    >
                      <Copy size={14} />
                      Copy
                    </button>
                  </div>
                  <button
                    onClick={shareOnWhatsApp}
                    className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white"
                    style={{ backgroundColor: '#25D366' }}
                    data-testid="share-whatsapp-button"
                  >
                    <WhatsappLogo size={18} weight="fill" />
                    Share on WhatsApp
                  </button>
                </div>
                <div className="flex gap-4 sm:gap-6">
                  <div className="text-center" data-testid="referral-patients">
                    <p className="text-3xl font-bold font-manrope">{referral.referred_patients}</p>
                    <p className="text-xs text-white/70 uppercase tracking-wider">Referrals</p>
                  </div>
                  <div className="text-center" data-testid="referral-consultations">
                    <p className="text-3xl font-bold font-manrope">{referral.paid_consultations}</p>
                    <p className="text-xs text-white/70 uppercase tracking-wider">Paid</p>
                  </div>
                  <div className="text-center" data-testid="referral-earnings">
                    <p className="text-3xl font-bold font-manrope">₹{referral.total_earnings.toLocaleString('en-IN')}</p>
                    <p className="text-xs text-white/70 uppercase tracking-wider">Earned</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Case Queue */}
          <div className="card">
            <Tabs defaultValue="pending" onValueChange={setActiveTab}>
              <TabsList className="mb-6">
                <TabsTrigger value="pending" data-testid="tab-pending">Pending ({pendingCases.length})</TabsTrigger>
                <TabsTrigger value="completed" data-testid="tab-completed">Completed ({completedCases.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="pending">
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  </div>
                ) : pendingCases.length === 0 ? (
                  <div className="text-center py-12" data-testid="no-pending-cases">
                    <Clock size={48} className="text-secondary mx-auto mb-4" weight="duotone" />
                    <p className="text-secondary">No pending cases</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingCases.map((caseItem, idx) => (
                      <Link
                        key={idx}
                        to={`/case/${caseItem.case_id || idx}`}
                        className="block border border-primary/20 rounded-lg p-6 hover:shadow-md transition-all"
                        data-testid={`pending-case-${idx}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-manrope font-semibold text-primary mb-1">
                              {caseItem.patient_name}
                            </h3>
                            <p className="text-sm text-secondary">
                              {caseItem.specialty} • {new Date(caseItem.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <span className={`status-badge status-${caseItem.status}`}>
                            {getStatusLabel(caseItem.status)}
                          </span>
                        </div>
                        <p className="text-secondary text-sm">{caseItem.current_diagnosis}</p>
                        <div className="mt-3 text-xs text-secondary">
                          <span className="inline-block px-2 py-1 bg-surface-secondary rounded">
                            {caseItem.tier === 'premium' ? 'Premium' : 'Free'}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="completed">
                {completedCases.length === 0 ? (
                  <div className="text-center py-12" data-testid="no-completed-cases">
                    <FileText size={48} className="text-secondary mx-auto mb-4" weight="duotone" />
                    <p className="text-secondary">No completed cases yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {completedCases.map((caseItem, idx) => (
                      <Link
                        key={idx}
                        to={`/case/${caseItem.case_id || idx}`}
                        className="block border border-primary/20 rounded-lg p-6 hover:shadow-md transition-all"
                        data-testid={`completed-case-${idx}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-manrope font-semibold text-primary mb-1">
                              {caseItem.patient_name}
                            </h3>
                            <p className="text-sm text-secondary">
                              {caseItem.specialty} • {new Date(caseItem.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <span className="status-badge status-opinion_ready">
                            {getStatusLabel(caseItem.status)}
                          </span>
                        </div>
                        <p className="text-secondary text-sm">{caseItem.current_diagnosis}</p>
                      </Link>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}
